import Icon, { IconType } from "./Icon";
import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  FC,
  Fragment,
  ReactNode,
  useContext,
  ReactElement,
} from "react";
import { RenderLink } from "../types";
import { OpenContext, RenderLinkContext, SelectContext } from "../lib/context";
import { classNames } from "../lib/utils";
import { LinkStrongIcon } from "src/monet/icons";

export type ListItemType = any;

function getListItemWrapperStyles(selected: boolean, disabled?: boolean) {
  return classNames(
    "command-palette-list-item block w-full text-left px-3.5 py-2.5 rounded-md text-white hover:bg-white/10 flex items-center space-x-2.5 justify-between",
    selected && !disabled
      ? "bg-white/10"
      : "bg-transparent",
    disabled
      ? "cursor-default pointer-events-none opacity-50"
      : "cursor-pointer"
  );
}

interface ListItemBaseProps {
  closeOnSelect?: boolean;
  icon?: FC | any;
  iconType?: IconType;
  showType?: boolean;
  disabled?: boolean;
  keywords?: string[];
  index: number;
}

export interface LinkProps
  extends ListItemBaseProps,
  DetailedHTMLProps<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > {
  renderLink?: RenderLink;
}

export function Link({
  renderLink: localRenderLink,
  closeOnSelect = true,
  disabled = false,
  showType = true,
  className,
  children,
  onClick,
  index,
  icon,
  ...rest
}: LinkProps) {
  const { renderLink: globalRenderLink } = useContext(RenderLinkContext);
  const { onChangeOpen } = useContext(OpenContext);
  const { selected } = useContext(SelectContext);

  const renderLink = localRenderLink || globalRenderLink;

  function renderLinkContent() {
    return (
      <ListItemContent
        type={<LinkStrongIcon />}
        icon={icon}
      >
        {children}
      </ListItemContent>
    );
  }

  const styles = classNames(
    getListItemWrapperStyles(selected === index, disabled),
    className
  );

  function clickAndClose(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    if (rest.href && !disabled) {
      if (onClick) {
        onClick(e);
      }

      if (closeOnSelect) {
        onChangeOpen(false);
      }
    }
  }

  return renderLink ? (
    <Fragment>
      {renderLink({
        ...rest,
        "data-close-on-select": closeOnSelect,
        children: renderLinkContent(),
        "aria-disabled": disabled,
        onClick: clickAndClose,
        className: styles,
      })}
    </Fragment>
  ) : (
    <a
      {...rest}
      data-close-on-select={closeOnSelect}
      aria-disabled={disabled}
      onClick={clickAndClose}
      className={styles}
    >
      {renderLinkContent()}
    </a>
  );
}

export interface ButtonProps
  extends ListItemBaseProps,
  DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > { }

export function Button({
  closeOnSelect = true,
  showType = true,
  className,
  children,
  iconType,
  onClick,
  index,
  icon,
  ...rest
}: ButtonProps) {
  const { selected } = useContext(SelectContext);
  const { onChangeOpen } = useContext(OpenContext);

  function clickAndClose(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (onClick) {
      onClick(e);

      if (closeOnSelect) {
        onChangeOpen(false);
      }
    }
  }

  return (
    <button
      {...rest}
      aria-disabled={rest.disabled ?? false}
      data-close-on-select={closeOnSelect}
      onClick={clickAndClose}
      className={classNames(
        getListItemWrapperStyles(selected === index, rest.disabled),
        className
      )}
    >
      <ListItemContent
        type={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.81582 9.44L6.79089 11.1704C6.99826 11.3521 7.00815 11.6559 6.81298 11.849C6.6178 12.0421 6.29148 12.0512 6.08411 11.8695L3.16224 9.30951C3.05871 9.21882 3 9.09235 3 8.96C3 8.82765 3.05871 8.70119 3.16224 8.61043L6.08411 6.05046C6.29148 5.86877 6.6178 5.87798 6.81298 6.07103C7.00815 6.26407 6.99826 6.56785 6.79089 6.74954L4.81581 8.48H11.7656C12.4301 8.48 12.9688 7.97856 12.9688 7.36V4.48C12.9688 4.21491 13.1996 4 13.4844 4C13.7691 4 14 4.21491 14 4.48V7.36C14 8.50874 12.9996 9.44 11.7656 9.44H4.81582Z" fill="currentColor" />
        </svg>}
        icon={icon}
      >
        {children}
      </ListItemContent>
    </button>
  );
}

interface ListItemContentProps {
  icon?: any;
  children: ReactNode;
  type?: ListItemType;
}

function ListItemContent({
  icon,
  children,
  type,
}: ListItemContentProps) {
  return (
    <>
      <div className="flex w-full items-center space-x-2.5">
        {icon && icon}
        {typeof children === "string" ? (
          <span className="truncate max-w-md">{children}</span>
        ) : (
          children
        )}
      </div>

      {type && <span className="text-white text-sm">{type}</span>}
    </>
  );
}

export default function ListItem(props: ButtonProps & LinkProps) {
  const Wrapper = props.href ? Link : Button;

  return <Wrapper {...props} />;
}